import React, { useEffect, useRef, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import FooterLanding from "../../components/FooterLanding/FooterLanding";
import styles from "./HelpPage.module.css";
import { ReactComponent as HelpIcon } from "../../assets/HelpIcon.svg";
import KIcon from "../../assets/KIcon.svg";
import { ReactComponent as Arrow } from "../../assets/BlackDiagonalArrow.svg";
import { ReactComponent as SettingGreenIcon } from "../../assets/SettingGreenIcon.svg";
import { ReactComponent as TeamWithHearthGreenIcon } from "../../assets/TeamWithHearthGreenIcon.svg";
import { ReactComponent as GreenDotsAppIcon } from "../../assets/GreenDotsAppIcon.svg";
import { ReactComponent as GreenFolderIcon } from "../../assets/GreenFolderIcon.svg";
import { ReactComponent as ContactsGreenIcon } from "../../assets/ContactsGreenIcon.svg";
import { ReactComponent as AssetsGreenIcon } from "../../assets/AssetsGreenIcon.svg";
import { ReactComponent as TransactionsGreenIcon } from "../../assets/TransactionsGreenIcon.svg";
import { ReactComponent as ControlPanelGreenIcon } from "../../assets/ControlPanelGreenIcon.svg";
import { ReactComponent as SecurityGreenIcon } from "../../assets/SecurityGreenIcon.svg";
import useFocusShortcut from "../../../../utils/useFocusShortcut";
import SearchIconWithIcon from "../../components/SearchIconWithIcon/SearchIconWithIcon";
import TemplateArticleHelp from "./TemplateArticleHelp/TemplateArticleHelp";
import YourAccount from "./YourAccount/YourAccount";
import Team from "./Team/Team";
import App from "./Apps/Apps";
import DocumentManagement from "./DocumentManagement/DocumentManagement";
import ContactManagement from "./ContactManagement/ContactManagement";
import AssetManagement from "./AssetManagement/AssetManagement";
import Transactions from "./Transactions/Transactions";
import ControlPanel from "./ControlPanel/ControlPanel";
import Security from "./Security/Security";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const HelpPage = () => {
  const { t } = useTranslation(["helpPage", "ChatView"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const searchInputRef = useRef();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  useFocusShortcut(searchInputRef, "k");
  const [isFocused, setIsFocused] = useState(false);

  const articles = [
    {
      icon: (
        <>
          <SettingGreenIcon />
        </>
      ),
      title: t("yourAccount"),
      quantity: 5,
    },
    {
      icon: (
        <>
          <TeamWithHearthGreenIcon />
        </>
      ),
      title: t("team"),
      quantity: 3,
    },
    {
      icon: (
        <>
          <GreenDotsAppIcon />
        </>
      ),
      title: t("Apps"),
      quantity: 3,
    },
    {
      icon: (
        <>
          <GreenFolderIcon />
        </>
      ),
      title: t("documentManagement"),
      quantity: 3,
    },
    {
      icon: (
        <>
          <ContactsGreenIcon />
        </>
      ),
      title: t("contactManagement"),
      quantity: 5,
    },
    {
      icon: (
        <>
          <AssetsGreenIcon />
        </>
      ),
      title: t("assetManagement"),
      quantity: 5,
    },
    {
      icon: (
        <>
          <TransactionsGreenIcon />
        </>
      ),
      title: t("transactions"),
      quantity: 6,
    },
    {
      icon: (
        <>
          <ControlPanelGreenIcon />
        </>
      ),
      title: t("controlPanel"),
      quantity: 3,
    },
    {
      icon: (
        <>
          <SecurityGreenIcon />
        </>
      ),
      title: t("security"),
      quantity: 3,
    },
  ];

  const categories = [
    {
      id: "yourAccount",
      name: t("yourAccount"),
      articles: [
        { title: t("currentPlanHistory") },
        { title: t("language") },
        { title: t("currency") },
        { title: t("chats") },
        { title: t("connections") },
      ],
    },
    {
      id: "team",
      name: t("team"),
      articles: [
        { title: t("accessPermissions") },
        { title: t("timeTrackingDigitalSignConfig") },
        { title: t("mergePlan") },
      ],
    },
    {
      id: "Apps",
      name: t("Apps"),
      articles: [
        { title: t("createYourGPTs") },
        { title: t("automate") },
        { title: t("notifications") },
      ],
    },
    {
      id: "documentManagement",
      name: t("documentManagement"),
      articles: [
        { title: t("fileUpload") },
        { title: t("navigateFolders") },
        { title: t("classificationConcepts") },
        { title: t("quickActions") },
      ],
    },
    {
      id: "contactManagement",
      name: t("contactManagement"),
      articles: [
        { title: t("contactRegistrationControl") },
        { title: t("importContacts") },
        { title: t("settingsParameters") },
        { title: t("exportContacts") },
        { title: t("historyTraceability") },
      ],
    },
    {
      id: "assetManagement",
      name: t("assetManagement"),
      articles: [
        { title: t("assetRegistrationControl") },
        { title: t("discountsTaxes") },
        { title: t("customSettings") },
        { title: t("exportAssets") },
        { title: t("historyTraceability") },
      ],
    },
    {
      id: "transactions",
      name: t("transactions"),
      articles: [
        { title: t("invoiceManagement") },
        { title: t("transactionQueries") },
        { title: t("documentApproval") },
        { title: t("transactionStatuses") },
        { title: t("paymentAutomation") },
        { title: t("stripeRefundsInProgress") },
      ],
    },
    {
      id: "controlPanel",
      name: t("controlPanel"),
      articles: [
        { title: t("kpiMonitoring") },
        { title: t("incomeExpenses") },
        { title: t("timeTrackingManagement") },
      ],
    },
    {
      id: "security",
      name: t("security"),
      articles: [
        { title: t("documentCustody") },
        { title: t("deleteAccount") },
        { title: t("passwordRecovery") },
      ],
    },
  ];

  const filteredCategories = categories
    .map((category) => {
      const matchesCategory = category.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const filteredArticles = category.articles.filter((article) =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (matchesCategory) {
        return category;
      } else if (filteredArticles.length > 0) {
        return { ...category, articles: filteredArticles };
      }

      return null; 
    })
    .filter(Boolean); 

  const categoryComponents = {
    yourAccount: <YourAccount setSelectedCategory={setSelectedCategory} />,
    team: <Team setSelectedCategory={setSelectedCategory} />,
    Apps: <App setSelectedCategory={setSelectedCategory} />,
    documentManagement: (
      <DocumentManagement setSelectedCategory={setSelectedCategory} />
    ),
    contactManagement: (
      <ContactManagement setSelectedCategory={setSelectedCategory} />
    ),
    assetManagement: (
      <AssetManagement setSelectedCategory={setSelectedCategory} />
    ),
    transactions: <Transactions setSelectedCategory={setSelectedCategory} />,
    controlPanel: <ControlPanel setSelectedCategory={setSelectedCategory} />,
    security: <Security setSelectedCategory={setSelectedCategory} />,
  };

  useEffect(() => {
    if (!categoryId) return;

    const matched = categories.find(
      (cat) => cat.id.toLowerCase() === categoryId.toLowerCase()
    );

    if (matched) {
      setSelectedCategory(matched.id);
    }
  }, [categoryId]);
  React.useEffect(
    () => {
      const translationId = localStorage.getItem("translationId");
      document.title = `${translationId?.replace(translationId
          ?.slice(translationId?.length - 3, translationId?.length),'GPT')
          ?.toUpperCase() || t("ChatView:facturaGPT")} - ${t('helpCenter')}`;
    },[]
  )

  return (
    <div className={styles.HelpPage}>
      <Navbar />
      <div className={styles.HelpPageContent}>
        <h2>
          <HelpIcon /> {t("helpCenter")}
        </h2>
        <div className={styles.searchArticleContainer}>
          <h3>{t("howCanWeHelp")}</h3>
          <p>{t("detailedInfo")}</p>
          <div className={styles.inputContainer}>
            <SearchIconWithIcon
              ref={searchInputRef}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              classNameIconRight={styles.searchContainerL}
              placeholder={t("searchPlaceholder")}
              onFocusP={() => setIsFocused(true)}
              onBlurP={() => setIsFocused(false)}
            >
              <img
                src={KIcon}
                alt="filterIcon"
                className={styles.searchContainerIcon}
              />
            </SearchIconWithIcon>
            {searchTerm && isFocused && filteredCategories.length > 0 && (
              <div className={styles.recomendQuestions}>
                {filteredCategories.map((category, index) => (
                  <div key={index} className={styles.recommendationItem}>
                    {category.articles.map((article, articleIndex) => (
                      <div
                        key={articleIndex}
                        onMouseDown={() => {
                          navigate(`/help/${category.id}`);
                          setSelectedCategory(category.id);
                        }}
                        className={styles.articleItem}
                      >
                        <div>
                          <p>
                            <strong>{category.name}</strong>
                          </p>
                          <span>{article.title}</span>
                        </div>
                        <Arrow />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {!selectedCategory ? (
          <div className={styles.articlesContainer}>
            {articles.map((article, index) => {
              const category = categories.find(
                (cat) => cat.name === article.title
              );
              return (
                <div
                  key={index}
                  className={styles.articleCard}
                  onMouseDown={() => {
                    navigate(`/help/${category.id}`);
                    setSelectedCategory(category.id);
                  }}
                >
                  <div className={styles.articleIcon}>{article.icon}</div>
                  <h4>{article.title}</h4>
                  <p>
                    {article.quantity} {t("articles")}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          categoryComponents[selectedCategory] || <p>{t("categoryNotFound")}</p>
        )}
      </div>
      <FooterLanding />
    </div>
  );
};

export default HelpPage;
